import { ContentService } from '@blk/cwp-ng-framework';
import * as unzip from 'lodash/unzip';

import { CellUtil } from '../../utils';
import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { ColumnText } from '../column-text';
import { ColumnExtractor } from '../extractors/column-extractor';
import { StoreColumnExtractor } from '../extractors/store-column-extractor';
import { SeriesMapper } from '../series-mappers/series-mapper.interface';
import { TableData } from '../table-data';
import { AsIsTableDataTransform } from '../table-data-transform/as-is-table-data-transform';
import { TableDataTransform } from '../table-data-transform/table-data-transform';
import { SeriesExtractionExtractorConfig } from './series-extractor-config';

export class TablePerPortfolioSeriesExtractorConfig extends SeriesExtractionExtractorConfig {
	constructor(
		mapper: SeriesMapper,
		public firstColumnTitle: ColumnText,
		public readonly columns: ColumnConfig<ColumnExtractor>[],
		portfolioName: ColumnConfig<StoreColumnExtractor>,
		childMapper?: (parent: any) => any[],
		tableDataTransform: TableDataTransform = AsIsTableDataTransform.instance,
		public readonly childColumns: ColumnConfig<ColumnExtractor>[] = columns
	) {
		super(portfolioName, mapper, childMapper, tableDataTransform);
		if (childColumns.length !== columns.length) {
			throw new Error('Child columns number must match the number of regular columns');
		}
	}

	public storeDependencies(): string[] {
		return this.mapper.getStoreDependencies();
	}

	public extract(storeLookup: Map<string, any[]>, contentSvc: ContentService): TableData[] {
		const dataArgs = this.mapper.getStoreDependencies().map(config => storeLookup.get(config));
		const portsDataArgs = unzip(dataArgs);
		return portsDataArgs.map((portfolioData, portfolioIndex) => {
			const cells: CellData[][] = [];
			this.mapper.mapper(...portfolioData).forEach(data => {
				const parent = this.columns.map(column => {
					// translate the column config (label and info bubble) at the cell level
					column = CellUtil.translateColumnConfig(column, contentSvc);

					const value = column.extractor.extractor()(data);
					return new CellData(column, value, column.labelFormatter.format(value, contentSvc), null, this.generateAttributes(value, column, contentSvc));
				});

				cells.push(parent);
				if (this.childMapper) {
					this.childMapper(data).forEach(child => {
						cells.push(
							this.childColumns.map(childExp => {
								// translate the column config (label and info bubble) at the child cell level
								childExp = CellUtil.translateColumnConfig(childExp, contentSvc);

								const value = childExp.extractor.extractor()(child);
								return new CellData(
									childExp,
									value,
									childExp.labelFormatter.format(value, contentSvc),
									parent,
									this.generateAttributes(value, childExp, contentSvc)
								);
							})
						);
					});
				}
			});
			const filteredLookup = new Map();
			storeLookup.forEach((value, key) => filteredLookup.set(key, [value[portfolioIndex]]));
			const portfolioNames: CellData[] = this.extractStoreColumn(this.portfolioName, filteredLookup, contentSvc);
			const columnNames: CellData[] = this.generateColumnHeaders(this.columns, contentSvc);

			const tableData = new TableData(columnNames, portfolioNames, cells, portfolioNames, this.translateColumnText(this.firstColumnTitle, contentSvc));
			const transformedCells: TableData = this.tableDataTransform.transform(tableData, contentSvc);

			return transformedCells;
		});
	}

	public getDataPointPath(): any {
		return '';
	}
}
